import {
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
  useRxCollectionEpocusEncounter,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import {
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-exam";
import {
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-review";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useUser } from "$global-providers/user-provider/provider";
import { useCallback, useMemo } from "react";
import type { RxCollection } from "rxdb";
import { useRxData } from "rxdb-hooks";
import { useCan } from "./rbac/use-can";
import { MATCH_NOTHING } from "./rxdb-selectors";

export type ReviewerQueueEntry = {
  encounter: EpocusEncounter;
  exams: EpocusExam[];
  fromPool: boolean;
  hasMyDraft: boolean;
};

export type ReviewerQueue = {
  entries: ReviewerQueueEntry[];
  pendingCount: number;
  poolCount: number;
  loading: boolean;
};

export const useReviewerQueue = (): ReviewerQueue => {
  const { id: userId } = useUser();
  const canViewReviewQueue = useCan("viewReviewQueue");

  const encountersQuery = useCallback(
    (collection: RxCollection<EpocusEncounter>) =>
      collection.find({
        selector: canViewReviewQueue
          ? { status: { $eq: "pending_review" } }
          : MATCH_NOTHING,
        sort: [{ date: "asc" }],
      }),
    [canViewReviewQueue],
  );
  const { result: pendingEncounters, isFetching } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    encountersQuery,
  );

  const mine = useMemo(
    () =>
      pendingEncounters.filter(
        (encounter) =>
          encounter.performerId !== userId &&
          ((encounter.assignedReviewerId ?? null) === null ||
            encounter.assignedReviewerId === userId),
      ),
    [pendingEncounters, userId],
  );

  const encounterIds = useMemo(() => mine.map(({ id }) => id), [mine]);

  const examsQuery = useCallback(
    (collection: RxCollection<EpocusExam>) =>
      encounterIds.length > 0
        ? collection.find({ selector: { encounterId: { $in: encounterIds } } })
        : collection.find({ selector: MATCH_NOTHING }),
    [encounterIds],
  );
  const { result: exams } = useRxData<EpocusExam>(
    RXDB_EPOCUS_EXAM_COLLECTION_NAME,
    examsQuery,
  );

  const examIds = useMemo(() => exams.map(({ id }) => id), [exams]);

  const draftsQuery = useCallback(
    (collection: RxCollection<EpocusReview>) =>
      examIds.length > 0
        ? collection.find({
            selector: {
              examId: { $in: examIds },
              reviewerId: { $eq: userId },
            },
          })
        : collection.find({ selector: MATCH_NOTHING }),
    [examIds, userId],
  );
  const { result: myReviews } = useRxData<EpocusReview>(
    RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
    draftsQuery,
  );

  return useMemo(() => {
    const draftExamIds = new Set(
      myReviews
        .filter((review) => review.signedAt == null)
        .map((review) => review.examId),
    );

    const examsByEncounter = new Map<string, EpocusExam[]>();
    for (const exam of exams) {
      const list = examsByEncounter.get(exam.encounterId);
      if (list) list.push(exam);
      else examsByEncounter.set(exam.encounterId, [exam]);
    }

    const entries = mine.map((encounter) => {
      const encounterExams = examsByEncounter.get(encounter.id) ?? [];
      return {
        encounter,
        exams: encounterExams,
        fromPool: (encounter.assignedReviewerId ?? null) === null,
        hasMyDraft: encounterExams.some(({ id }) => draftExamIds.has(id)),
      };
    });

    return {
      entries,
      pendingCount: entries.length,
      poolCount: entries.filter(({ fromPool }) => fromPool).length,
      loading: isFetching,
    };
  }, [mine, exams, myReviews, isFetching]);
};

export const useClaimForReview = (): ((
  encounterId: string,
) => Promise<void>) => {
  const { id: userId } = useUser();
  const { getCurrentTime } = useServerTime();
  const collection = useRxCollectionEpocusEncounter();

  return useCallback(
    async (encounterId: string) => {
      if (!collection) return;
      const doc = await collection.findOne(encounterId).exec();
      if (!doc) return;
      if ((doc._data.assignedReviewerId ?? null) !== null) return;

      const now = getCurrentTime().toISOString();
      await doc.incrementalModify((data) => ({
        ...data,
        assignedReviewerId: userId,
        assignedAt: now,
        meta: { ...data.meta, updatedAt: now, updatedByUserId: userId },
      }));
    },
    [collection, userId, getCurrentTime],
  );
};
